/**
 * audio-mixdown.js — window.AudioMixdown
 *
 * Свести несколько аудиодорожек в ОДНУ дорожку заданной длины теми же
 * правилами, по которым это делает граф FFmpeg наших тулов:
 *
 *     atrim → aloop(+перекрёстное затухание) → adelay → volume → afade → amix(normalize=0)
 *
 * ЗАЧЕМ (замер 16.09.2026, /add-music-to-video). Экспорт «видео + музыка» не
 * трогает картинку вообще (`-c:v copy`), и вся его цена — декод и кодирование
 * ЗВУКА в wasm плюс прогон файла через кучу. На НАСТОЯЩЕЙ съёмке (3 мин, 720p,
 * 69 МБ, H.264+AAC 48 кГц) в настоящем Chrome без замедления:
 *
 *     FFmpeg-WASM, весь экспорт …………………………………… 68.5 с
 *     нативно: декод музыки ………………………………………… 1.5 с
 *     нативно: сведение (этот модуль) ……………………………… 0.2 с
 *     нативно: AAC + ремукс без копии (VideoAudioLane) ……… 3.2 с
 *     ──────────────────────────────────────────────────────────
 *     итого нативно …………………………………………………………… 4.9 с   (в 14 раз быстрее)
 *
 * При замедлении 4× (класс телефона) нативный путь — 13.9 с. По телеметрии RU
 * за 30 дней это чинит: медиана экспорта на телефонах 19.2 с, p90 180 с, p99
 * 998 с, 1503 прогона длиннее минуты (82 % из них — телефоны) и 508 сессий,
 * брошенных прямо во время обработки.
 *
 * ДВА СЛОЯ РАЗДЕЛЕНЫ НАМЕРЕННО:
 *   plan(opts)   — ЧИСТЫЙ расчёт: какой кусок какого буфера когда звучит и с
 *                  какой огибающей громкости. Ничего не создаёт, не зависит от
 *                  Web Audio и потому проверяется узловыми тестами, которые
 *                  гоняются в суите на КАЖДОМ прогоне (tests/js/audio-mixdown).
 *   render(opts) — исполнение плана в OfflineAudioContext. Микшер и ресемплер
 *                  здесь браузерные: они уже правильные, и переписывать их
 *                  вручную значило бы завести второй источник ошибок.
 *
 * ⚠️ Чего этот модуль НЕ делает: изменение скорости с сохранением высоты тона
 * (atempo). Web Audio такого не умеет — `playbackRate` тянет тон за собой.
 * `unsupported()` называет это причиной, и вызывающий либо уходит на
 * FFmpeg-лейн, либо сам приводит дорожку к скорости 1 ДО сведения: растягивает
 * отрезок тем же фильтром, что стоял бы в графе, и отдаёт его как дорожку
 * «обрезка 0…D, скорость 1» во ВЫХОДНОМ времени (/add-music-to-video,
 * amStretchPlan — с 02.10.2026).
 *
 *   var bad = AudioMixdown.unsupported(tracks);      // [] — можно нативно
 *   var p   = AudioMixdown.plan({ tracks, durationSec, sampleRate, channels });
 *   var buf = await AudioMixdown.render({ tracks, plan: p }, { signal });
 */
(function (w) {
    'use strict';

    /* Дорожка считается неслышимой ровно по тем же правилам, по которым тул
       исключает её из графа FFmpeg: иначе два лейна разошлись бы на тишине. */
    function silentReason(t, durationSec) {
        if (!t) return 'missing';
        if (t.muted) return 'muted';
        if (!(t.volume > 0.02)) return 'vol0';
        if (durationSec > 0 && (t.offsetSec || 0) >= durationSec - 0.05) return 'offside';
        if (!t.loop && clipSec(t) < 0.3) return 'clip0';
        return null;
    }

    function clipSec(t) {
        var srcDur = (t.buffer && t.buffer.duration) || t.durationSec || 0;
        var from = Math.max(0, t.trimStartSec || 0);
        var to = t.trimEndSec > 0 ? Math.min(t.trimEndSec, srcDur || t.trimEndSec) : srcDur;
        var d = to - from;
        return d > 0 ? d : 0;
    }

    /** Причины, по которым нативное сведение невозможно (пусто — можно). */
    function unsupported(tracks, durationSec) {
        var out = [];
        for (var i = 0; i < (tracks || []).length; i++) {
            var t = tracks[i];
            if (!t || silentReason(t, durationSec || 0)) continue;
            if (t.speed && Math.abs(t.speed - 1) > 0.005 && out.indexOf('speed') < 0) out.push('speed');
        }
        return out;
    }

    /* Линейная огибающая: точки {t, g} на ОБЩЕЙ шкале времени. Между точками —
       прямая; afade в FFmpeg по умолчанию тоже линеен (curve=tri). */
    function envFor(voiceStart, voiceDur, gain, fades) {
        var pts = [{ t: voiceStart, g: gain }, { t: voiceStart + voiceDur, g: gain }];
        function setAt(t, g) {
            t = Math.max(voiceStart, Math.min(voiceStart + voiceDur, t));
            for (var i = 0; i < pts.length; i++) {
                if (Math.abs(pts[i].t - t) < 1e-6) { pts[i].g = g; return; }
                if (pts[i].t > t) { pts.splice(i, 0, { t: t, g: g }); return; }
            }
            pts.push({ t: t, g: g });
        }
        for (var f = 0; f < fades.length; f++) {
            var fd = fades[f];
            if (!(fd.dur > 0.001)) continue;
            if (fd.type === 'in') { setAt(fd.at, 0); setAt(fd.at + fd.dur, gain); }
            else { setAt(fd.at, gain); setAt(fd.at + fd.dur, 0); }
        }
        return pts;
    }

    /**
     * ЧИСТЫЙ расчёт расписания.
     * tracks: [{ buffer|durationSec, volume, muted, offsetSec, trimStartSec,
     *            trimEndSec, fadeInSec, fadeOutSec, loop, crossfadeSec, speed,
     *            pan }]
     * `pan` (-1…1) читается только при отрисовке — на расписание он не влияет.
     * → { frames, sampleRate, channels, durationSec, voices, skipped }
     */
    function plan(o) {
        o = o || {};
        var tracks = o.tracks || [];
        var durationSec = +o.durationSec > 0 ? +o.durationSec : 0;
        var sampleRate = +o.sampleRate > 0 ? +o.sampleRate : 48000;
        var channels = +o.channels > 0 ? +o.channels : 2;
        var voices = [], skipped = [], i, t;

        for (i = 0; i < tracks.length; i++) {
            t = tracks[i];
            var why = silentReason(t, durationSec);
            if (why) { skipped.push({ track: i, why: why }); continue; }

            var gain = +t.volume;
            var start0 = Math.max(0, t.offsetSec || 0);
            var srcStart = Math.max(0, t.trimStartSec || 0);
            var clip = clipSec(t);
            if (!(clip > 0)) { skipped.push({ track: i, why: 'clip0' }); continue; }

            /* Петля: повторяем клип от смещения до конца видео. Перекрёстное
               затухание в FFmpeg стоит ВНУТРИ повторяемого куска (afade на
               каждом проходе), поэтому и здесь оно своё у каждого повтора. */
            var cf = (t.loop && t.crossfadeSec > 0.01) ? Math.min(t.crossfadeSec, clip / 2) : 0;
            var span = durationSec > 0 ? durationSec : start0 + clip;
            var reps = t.loop ? Math.max(1, Math.ceil((span - start0) / clip)) : 1;
            /* Затухания САМОЙ дорожки считаются от её начала и кончаются там, где
               её перестаёт быть слышно: у петли и у дорожки длиннее сведения — на
               его конце. Прежде затухание длинной дорожки стояло на конце ЕЁ
               клипа, за краем сведения, и не звучало вовсе: песня 5:01 на видео
               2:38 обрывалась на полуслове при любом «затухании» (комментарий
               #1844, 06.10.2026; так у 58 % экспортов /add-music-to-video). Граф
               FFmpeg тула ставит `afade=t=out` туда же — на конец звучащего. */
            var trackDur = Math.max(0, Math.min(t.loop ? span - start0 : clip, span - start0));
            var fadeIn = Math.min(t.fadeInSec || 0, trackDur);
            var fadeOut = Math.min(t.fadeOutSec || 0, trackDur);

            for (var r = 0; r < reps; r++) {
                var vStart = start0 + r * clip;
                if (durationSec > 0 && vStart >= durationSec - 0.001) break;
                var vDur = clip;
                if (durationSec > 0 && vStart + vDur > durationSec) vDur = durationSec - vStart;
                if (!(vDur > 0.001)) break;

                var fades = [];
                if (cf > 0) {
                    fades.push({ type: 'in', at: vStart, dur: Math.min(cf, vDur) });
                    if (vDur > cf) fades.push({ type: 'out', at: vStart + vDur - cf, dur: cf });
                }
                if (fadeIn > 0 && vStart < start0 + fadeIn) fades.push({ type: 'in', at: start0, dur: fadeIn });
                if (fadeOut > 0 && vStart + vDur > start0 + trackDur - fadeOut) {
                    fades.push({ type: 'out', at: start0 + trackDur - fadeOut, dur: fadeOut });
                }
                voices.push({ track: i, start: vStart, srcStart: srcStart, dur: vDur, env: envFor(vStart, vDur, gain, fades) });
            }
        }

        return {
            durationSec: durationSec, sampleRate: sampleRate, channels: channels,
            frames: Math.max(1, Math.round(durationSec * sampleRate)),
            voices: voices, skipped: skipped
        };
    }

    /** Громкость голоса в момент t (для тестов и отладки): линейно между точками. */
    function gainAt(voice, t) {
        var p = voice && voice.env;
        if (!p || !p.length) return 0;
        if (t <= p[0].t) return p[0].g;
        for (var i = 1; i < p.length; i++) {
            if (t <= p[i].t) {
                var dt = p[i].t - p[i - 1].t;
                if (dt <= 1e-9) return p[i].g;
                return p[i - 1].g + (p[i].g - p[i - 1].g) * ((t - p[i - 1].t) / dt);
            }
        }
        return p[p.length - 1].g;
    }

    /** Исполнение плана. Микшер и ресемплер — браузерные. */
    /* ── Приглушение музыки под звук видео ──────────────────────────────────
       Огибающая усиления для дорожек, которые должны уходить вниз, пока в
       ключевой дорожке (звук самого видео) есть звук.

       Числа не на глаз. Пороги те же, что замерены для /video-audio
       (threshold=0.05, ratio=8): на настоящей речи это 10.9 дБ приглушения —
       столько же ставят вручную на монтаже. Остальное подобрано замером против
       самого FFmpeg на той же речи (8 с, 4 начала слов):

         вариант                        под речью   первые 40 мс слова   в паузах
         FFmpeg sidechaincompress        −10.7 дБ        −6.3 дБ          −1.6 дБ
         наш (СКЗ, заглядывание 60 мс)   −11.0 дБ        −8.6 дБ          −2.1 дБ
         наш с пиковым детектором        −14.2 дБ       −13.1 дБ          −2.5 дБ

       То есть по слышимой глубине лейны совпадают (0.3 дБ — неразличимо), а
       ЗАГЛЯДЫВАНИЕ ВПЕРЁД даёт то, чего причинный компрессор дать не может:
       к первым 40 мс слова музыка уже на 2.3 дБ ниже, а не ныряет после
       первого слога. Пиковый детектор отвергнут: он совпал с FFmpeg на чистом
       тоне и переглушил на речи (у речи высокий пик-фактор).

       ⚠️ Сглаживать дважды нельзя: отпуск 400 мс И в детекторе, И в усилении
       оставлял музыку внизу на −10.2 дБ там, где должно быть 0. Детектор —
       короткое СКЗ (10 мс), времена атаки и отпуска принадлежат УСИЛЕНИЮ. */
    var DUCK = { threshold: 0.05, ratio: 8, detectMs: 10, attackMs: 20, releaseMs: 250, lookaheadMs: 60 };

    function duckEnvelope(key, sampleRate, o) {
        /* Частичная настройка ДОПОЛНЯЕТ умолчания, а не заменяет их: вызов
           вида {releaseMs: 300} иначе обнулял бы порог и отношение и молча
           отдавал NaN на всю дорожку. */
        var d = DUCK, c = {};
        for (var kk in d) if (Object.prototype.hasOwnProperty.call(d, kk)) c[kk] = d[kk];
        if (o) for (var kk2 in o) if (Object.prototype.hasOwnProperty.call(o, kk2) && o[kk2] != null) c[kk2] = o[kk2];
        o = c;
        var n = key.length;
        if (!n) return new Float32Array(0);

        /* ВСЁ считаем по сетке 2 кГц, а не по сэмплам. Детектор и так усредняет
           10 мс, отпуск живёт сотнями миллисекунд — на сэмплах здесь нечего
           разрешать, а цена разная: на трёхминутной дорожке (8.6 млн сэмплов)
           посэмпловая версия стоила 307 мс, сеточная — 111 мс при той же
           огибающей. Наружу сетка
           разворачивается линейно: ступенька 0.5 мс неслышна. */
        var hop = Math.max(1, Math.round(sampleRate / 2000));
        var gr = sampleRate / hop;                       // частота сетки
        var m = Math.ceil(n / hop) + 1;
        var det = Math.max(1, Math.round((o.detectMs / 1000) * sampleRate));
        var look = Math.max(0, Math.round((o.lookaheadMs / 1000) * gr));
        var exp = 1 - 1 / o.ratio;

        // 1) цель усиления на сетке
        var tg = new Float32Array(m), acc = 0, i, k = 0;
        for (i = 0; i < n; i++) {
            acc += key[i] * key[i];
            if (i >= det) acc -= key[i - det] * key[i - det];
            if (i % hop) continue;
            /* ⚠️ `acc` — скользящая сумма квадратов, и она копит ошибку округления:
               после громкого куска вычитание старых сэмплов уводит её в
               МИКРОСКОПИЧЕСКИЙ минус, а `Math.sqrt(-1e-12)` — это NaN, который
               дальше расходится по всей огибающей. Замер на настоящей речи:
               NaN начинался ровно на 1.337 с — там, где кончалось слово, — и
               вся музыка после этого места превращалась в NaN, то есть в
               тишину в выданном файле. */
            var env = Math.sqrt(Math.max(acc, 0) / Math.min(i + 1, det));
            tg[k++] = env <= o.threshold ? 1 : Math.pow(env / o.threshold, -exp);
        }
        while (k < m) { tg[k] = k ? tg[k - 1] : 1; k++; }

        // 2) заглядывание вперёд — скользящий минимум на окне look
        var win = new Float32Array(m);
        var dq = new Int32Array(m), head = 0, tail = 0;
        for (i = m - 1; i >= 0; i--) {
            while (tail > head && tg[dq[tail - 1]] >= tg[i]) tail--;
            dq[tail++] = i;
            while (dq[head] > i + look) head++;
            win[i] = tg[dq[head]];
        }

        // 3) сглаживание: вниз по атаке, вверх по отпуску
        var aA = Math.exp(-1 / ((o.attackMs / 1000) * gr));
        var aR = Math.exp(-1 / ((o.releaseMs / 1000) * gr));
        var cur = 1;
        for (i = 0; i < m; i++) {
            var t = win[i];
            cur = t < cur ? aA * cur + (1 - aA) * t : aR * cur + (1 - aR) * t;
            win[i] = cur;
        }

        // 4) обратно в сэмплы
        var g = new Float32Array(n);
        for (i = 0; i < n; i++) {
            var f = i / hop, j = f | 0, fr = f - j;
            var a1 = win[j], b1 = j + 1 < m ? win[j + 1] : a1;
            g[i] = a1 + (b1 - a1) * fr;
        }
        return g;
    }

    /* Один проход исполнения: голоса, прошедшие отбор. Отбор нужен приглушению —
       ему требуются ключ и музыка ПОРОЗНЬ, чтобы огибающая считалась по звуку
       видео, а применялась к музыке. */
    async function renderVoices(p, tracks, pick, opts) {
        var OAC = w.OfflineAudioContext || w.webkitOfflineAudioContext;
        if (!OAC) throw new Error('no-offline-context');
        var ctx = new OAC(p.channels, p.frames, p.sampleRate);

        for (var i = 0; i < p.voices.length; i++) {
            var v = p.voices[i], t = tracks[v.track];
            if (pick && !pick(v)) continue;
            if (!t || !t.buffer) continue;
            var src = ctx.createBufferSource();
            src.buffer = t.buffer;
            var g = ctx.createGain(), env = v.env;
            g.gain.setValueAtTime(env[0].g, Math.max(0, env[0].t));
            for (var e = 1; e < env.length; e++) g.gain.linearRampToValueAtTime(env[e].g, Math.max(0, env[e].t));
            /* Панорама — решение о МАРШРУТЕ, а не о расписании: план
               (`plan()`) от неё не зависит и остаётся тем же чистым расчётом,
               что сверялся с графом FFmpeg. Здесь это один узел между
               громкостью и выходом, ровно как `apan` стоял бы после `volume`.
               Узел ставится ТОЛЬКО когда панорама задана: лишний узел на
               каждый голос — это лишняя работа на каждом кадре у тулов,
               которые про панораму не просили. */
            src.connect(g);
            var pan = +t.pan || 0;
            if (pan && ctx.createStereoPanner && p.channels > 1) {
                var pn = ctx.createStereoPanner();
                pn.pan.value = Math.max(-1, Math.min(1, pan));
                g.connect(pn); pn.connect(ctx.destination);
            } else {
                g.connect(ctx.destination);
            }
            /* Длительность у источника задаётся ЗДЕСЬ, а не обрезкой буфера:
               так кусок берётся из исходного буфера без единой копии PCM. */
            src.start(v.start, v.srcStart, v.dur);
        }

        if (opts.signal && opts.signal.aborted) throw new Error('aborted');
        var buf = await ctx.startRendering();
        if (opts.signal && opts.signal.aborted) throw new Error('aborted');
        return buf;
    }

    async function render(o, opts) {
        o = o || {}; opts = opts || {};
        var tracks = o.tracks || [];
        var p = o.plan && o.plan.voices ? o.plan : plan(o);
        var duck = o.duck;

        var hasKey = false, hasRest = false;
        if (duck && duck.keyTrack != null) {
            for (var i = 0; i < p.voices.length; i++) {
                if (p.voices[i].track === duck.keyTrack) hasKey = true; else hasRest = true;
            }
        }
        /* Приглушать нечего или нечем — обычный один проход. */
        if (!hasKey || !hasRest) return renderVoices(p, tracks, null, opts);

        var keyBuf = await renderVoices(p, tracks, function (v) { return v.track === duck.keyTrack; }, opts);
        var musBuf = await renderVoices(p, tracks, function (v) { return v.track !== duck.keyTrack; }, opts);

        /* Ключ для детектора — сумма каналов ключевой дорожки: приглушение
           реагирует на звук видео целиком, а не на один канал. */
        var n = keyBuf.length, ch = keyBuf.numberOfChannels, c, j;
        var key = new Float32Array(n);
        for (c = 0; c < ch; c++) {
            var kd = keyBuf.getChannelData(c);
            for (j = 0; j < n; j++) key[j] += kd[j] * kd[j];
        }
        for (j = 0; j < n; j++) key[j] = Math.sqrt(key[j] / ch);

        var g = duckEnvelope(key, keyBuf.sampleRate, duck);
        for (c = 0; c < keyBuf.numberOfChannels; c++) {
            var out = keyBuf.getChannelData(c);
            var mus = musBuf.getChannelData(Math.min(c, musBuf.numberOfChannels - 1));
            for (j = 0; j < n; j++) out[j] += mus[j] * g[j];
        }
        if (opts.signal && opts.signal.aborted) throw new Error('aborted');
        return keyBuf;
    }

    /* Объявляемся ЧЕРЕЗ `window.`, а не через псевдоним `w`: так пишет соседний
       video-audio-lane.js, и так эту глобаль видит сторож имён во встроенных
       скриптах (tests/node/blade-inline-script-identifiers.test.mjs — он читает
       форму `window.X =`). Через псевдоним объявление невидимо, и сторож честно
       сообщил, что блейд читает имя, которого «нигде не объявлено». */
    window.AudioMixdown = {
        plan: plan,
        render: render,
        gainAt: gainAt,
        unsupported: unsupported,
        silentReason: silentReason,
        duckEnvelope: duckEnvelope,
        DUCK: DUCK
    };

    /* Узловые тесты берут модуль напрямую — ту же реализацию, что и браузер. */
    if (typeof module !== 'undefined' && module.exports) module.exports = window.AudioMixdown;

})(typeof window !== 'undefined' ? window : globalThis);
